import { useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";

const PLACEHOLDER_IMAGE = "/images/project-placeholder.jpg";

const ProjectHero = ({ project, index = 0 }) => {
  const [imageSource, setImageSource] = useState(
    project.images?.[0]?.path || PLACEHOLDER_IMAGE
  );

  const tags = Array.isArray(project.tags) ? project.tags : [];
  const categories = Array.isArray(project.categories)
    ? project.categories
    : [];

  const description =
    project.shortDescription ||
    project.description ||
    "More information about this project will be added soon.";

  const hasWebsite =
    typeof project.website === "string" &&
    project.website.trim() !== "";

  const hasGitHub =
    typeof project.github === "string" &&
    project.github.trim() !== "";

  const hasDetails =
    typeof project.slug === "string" &&
    project.slug.trim() !== "";

  const statusClasses =
    project.status === "In development"
      ? "border-amber-200 bg-amber-50 text-amber-700"
      : "border-emerald-200 bg-emerald-50 text-emerald-700";

  return (
    <motion.article
      initial={{
        opacity: 0,
        y: 35,
      }}
      whileInView={{
        opacity: 1,
        y: 0,
      }}
      viewport={{
        once: true,
        amount: 0.15,
      }}
      transition={{
        duration: 0.55,
        delay: Math.min(index * 0.05, 0.2),
        ease: "easeOut",
      }}
      className="group overflow-hidden rounded-3xl border border-zinc-200 bg-white shadow-sm transition duration-300 hover:border-zinc-300 hover:shadow-xl hover:shadow-zinc-200/70"
    >
      <div className="grid lg:grid-cols-[1.05fr_0.95fr]">
        {/* Project image */}
        <div className="relative min-h-72 overflow-hidden bg-zinc-100 lg:min-h-[430px]">
          <img
            src={imageSource}
            alt={
              project.images?.[0]?.caption ||
              `${project.title} project preview`
            }
            loading="lazy"
            onError={() => {
              if (imageSource !== PLACEHOLDER_IMAGE) {
                setImageSource(PLACEHOLDER_IMAGE);
              }
            }}
            className="absolute inset-0 h-full w-full object-cover transition duration-700 group-hover:scale-[1.025]"
          />

          <div className="absolute inset-0 bg-gradient-to-t from-zinc-950/35 via-transparent to-transparent" />

          {project.featured && (
            <span className="absolute left-5 top-5 rounded-full border border-white/40 bg-white/90 px-3 py-1.5 text-xs font-semibold uppercase tracking-wide text-zinc-900 shadow-sm backdrop-blur">
              Featured
            </span>
          )}
        </div>

        {/* Project content */}
        <div className="flex flex-col p-7 sm:p-9 lg:p-10">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <span
              className={`inline-flex rounded-full border px-3 py-1 text-xs font-semibold ${
                statusClasses
              }`}
            >
              {project.status || "Completed"}
            </span>

            {project.date && (
              <span className="text-sm font-medium text-zinc-500">
                {project.date}
              </span>
            )}
          </div>

          <h2 className="mt-6 text-3xl font-semibold tracking-tight text-zinc-950 sm:text-4xl">
            {project.title}
          </h2>

          <p className="mt-5 text-base leading-7 text-zinc-600 sm:text-lg">
            {description}
          </p>

          {tags.length > 0 && (
            <div className="mt-7">
              <p className="text-sm font-semibold text-zinc-900">
                Frameworks and languages
              </p>

              <div className="mt-3 flex flex-wrap gap-2">
                {tags.map((tag) => (
                  <span
                    key={tag}
                    className="rounded-full border border-zinc-200 bg-zinc-50 px-3 py-1.5 text-sm font-medium text-zinc-700"
                  >
                    {tag}
                  </span>
                ))}
              </div>
            </div>
          )}

          {categories.length > 0 && (
            <div className="mt-6">
              <p className="text-sm font-semibold text-zinc-900">
                Project type
              </p>

              <div className="mt-3 flex flex-wrap gap-2">
                {categories.map((category) => (
                  <span
                    key={category}
                    className="rounded-full bg-indigo-50 px-3 py-1.5 text-sm font-medium text-indigo-700"
                  >
                    {category}
                  </span>
                ))}
              </div>
            </div>
          )}

          <div className="mt-auto flex flex-wrap gap-3 pt-9">
            {hasDetails && (
              <Link
                to={`/projects/${project.slug}`}
                className="group/button inline-flex items-center gap-2 rounded-xl bg-zinc-950 px-5 py-3 font-semibold text-white transition hover:-translate-y-0.5 hover:bg-zinc-800"
              >
                View details

                <svg
                  width="17"
                  height="17"
                  viewBox="0 0 24 24"
                  fill="none"
                  aria-hidden="true"
                  className="transition-transform group-hover/button:translate-x-1"
                >
                  <path
                    d="M5 12H19M19 12L13 6M19 12L13 18"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </Link>
            )}

            {hasWebsite && (
              <a
                href={project.website}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 rounded-xl border border-zinc-300 bg-white px-5 py-3 font-semibold text-zinc-900 transition hover:-translate-y-0.5 hover:border-zinc-400 hover:bg-zinc-50"
              >
                Live demo

                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  aria-hidden="true"
                >
                  <path
                    d="M7 17L17 7M17 7H9M17 7V15"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </a>
            )}

            {hasGitHub && (
              <a
                href={project.github}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 rounded-xl border border-zinc-300 bg-white px-5 py-3 font-semibold text-zinc-900 transition hover:-translate-y-0.5 hover:border-zinc-400 hover:bg-zinc-50"
              >
                Source code
              </a>
            )}
          </div>
        </div>
      </div>
    </motion.article>
  );
};

export default ProjectHero;
